import { CircleCheck, X, LoaderCircle, CircleAlert } from 'lucide-react';

/**
 * A2F-651: confirmation dialog for the bulk "Acknowledge all" action on
 * the Manage Conflicts → Duplicates tab.
 *
 * Visually mirrors AcceptUnexpectedDialog (A2F-565) so the two bulk-action
 * confirmations feel like the same gesture across surfaces. Deliberately
 * simpler — no stale-state guard (the duplicates set rarely changes mid-
 * dialog the way observed sections can during ingestion), no per-item
 * preview list (the count alone is the decision input).
 */

interface AcknowledgeAllDuplicatesDialogProps {
  /** Number of unresolved duplicate groups about to be acknowledged. */
  count: number;
  /** In-flight flag for the primary button spinner + disabled state. */
  submitting: boolean;
  /** Generic error message; cleared by caller on retry. */
  error: string;
  onConfirm: () => Promise<void>;
  onClose: () => void;
}

export function AcknowledgeAllDuplicatesDialog({
  count,
  submitting,
  error,
  onConfirm,
  onClose,
}: AcknowledgeAllDuplicatesDialogProps) {
  const disabled = submitting || count === 0;

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50">
      <div className="bg-white rounded-xl shadow-xl w-full max-w-lg mx-4">
        {/* Header */}
        <div className="flex items-center justify-between px-6 py-4 border-b border-slate-200">
          <div className="flex items-center gap-2">
            <CircleCheck className="w-5 h-5 text-brand-orange" />
            <h2 className="text-lg font-bold text-brand-charcoal">
              Acknowledge {count} duplicate {count === 1 ? 'group' : 'groups'}
            </h2>
          </div>
          <button
            onClick={onClose}
            className="text-slate-400 hover:text-slate-600"
            aria-label="Close dialog"
          >
            <X className="w-5 h-5" />
          </button>
        </div>

        {/* Content */}
        <div className="px-6 py-4 space-y-4">
          <p className="text-sm text-slate-600">
            Marks every file in every unresolved duplicate group as <strong>kept</strong>.
            Use this when all files are valid as-submitted (e.g., everything in this IND
            was sent to the FDA exactly as it appears here).
          </p>
          <p className="text-xs text-slate-500">
            Audit-logged as a single bulk-acknowledgment. Per-group decisions remain
            available later via the &ldquo;Reopen&rdquo; action on each acknowledged
            group.
          </p>

          {error && (
            <div className="flex items-start gap-2 px-4 py-3 bg-red-50 border border-red-200 rounded-lg text-sm text-red-700">
              <CircleAlert className="w-4 h-4 shrink-0 mt-0.5" />
              <span>{error}</span>
            </div>
          )}
        </div>

        {/* Footer */}
        <div className="flex justify-end gap-3 px-6 py-4 border-t border-slate-200">
          <button
            onClick={onClose}
            disabled={submitting}
            className="px-4 py-2 text-sm font-medium text-slate-600 border border-slate-200 rounded-lg hover:bg-slate-50 disabled:opacity-40"
          >
            Cancel
          </button>
          <button
            onClick={onConfirm}
            disabled={disabled}
            className="inline-flex items-center gap-2 px-4 py-2 text-sm font-medium text-white bg-brand-orange rounded-lg hover:bg-orange-600 disabled:opacity-50"
          >
            {submitting && <LoaderCircle className="w-4 h-4 animate-spin" />}
            Acknowledge All
          </button>
        </div>
      </div>
    </div>
  );
}
